import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Users, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthLayout } from '@/components/auth/AuthLayout';

interface InviteDetails {
  invite_id: string;
  project_id: string;
  project_name: string;
  invite_email: string | null;
  role: string;
  inviter_name: string | null;
  is_authenticated: boolean;
  current_user_email: string | null;
}

export function InviteConfirmPage() {
  const { inviteId, secret } = useParams<{ inviteId: string; secret: string }>();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchInvite() {
      try {
        const response = await fetch(`/api/invite/${inviteId}/${secret}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          const data = await response.json();
          setInviteError(data.error || 'Invalid invitation link');
          setLoading(false);
          return;
        }

        const data = await response.json();
        setInvite(data);

        // If user is not authenticated, redirect to login page
        if (!data.is_authenticated) {
          window.location.href = `/auth/invite-login/${inviteId}/${secret}`;
        }
      } catch {
        setInviteError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    }

    fetchInvite();
  }, [inviteId, secret]);

  const handleAccept = async () => {
    setIsAccepting(true);
    setError(null);

    try {
      const response = await fetch(`/project-invite/${inviteId}/${secret}/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.redirected) {
        window.location.href = response.url;
        return;
      }

      if (!response.ok) {
        const text = await response.text();
        setError(text || 'Failed to accept invitation');
        setIsAccepting(false);
        return;
      }

      // If we get a JSON response with a redirect URL
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const data = await response.json();
        if (data.redirect) {
          window.location.href = data.redirect;
          return;
        }
      }

      // Fallback: redirect to the project
      window.location.href = `/project/${invite?.project_id}/servers`;
    } catch {
      setError('Failed to accept invitation. Please try again.');
      setIsAccepting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AuthLayout>
    );
  }

  if (inviteError || !invite) {
    return (
      <AuthLayout>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{inviteError || 'Invalid invitation'}</AlertDescription>
        </Alert>
        <div className="text-center">
          <Link to="/projects">
            <Button variant="outline">Go to Projects</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Invitation Banner */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Users className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">
              You're invited to join <span className="text-primary">{invite.project_name}</span>
            </p>
            {invite.inviter_name && (
              <p className="text-sm text-muted-foreground mt-1">
                Invited by {invite.inviter_name}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Confirmation Card */}
      <div className="text-center py-4">
        <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          Accept Project Invitation
        </h1>
        <p className="text-muted-foreground mb-2">
          You're logged in as <span className="font-medium text-gray-900">{invite.current_user_email}</span>
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          You'll be added as a <span className="font-medium">{invite.role}</span> on this project.
        </p>

        <div className="space-y-3">
          <Button
            onClick={handleAccept}
            className="w-full h-11"
            disabled={isAccepting}
          >
            {isAccepting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Joining project...
              </>
            ) : (
              'Accept Invitation'
            )}
          </Button>

          <Link to="/projects">
            <Button variant="outline" className="w-full">
              Decline
            </Button>
          </Link>
        </div>
      </div>

      {/* Different Account Link */}
      <div className="mt-6 pt-6 border-t text-center">
        <p className="text-sm text-muted-foreground mb-2">
          Want to join with a different account?
        </p>
        <a
          href="/auth/logout"
          className="text-sm text-primary hover:underline"
        >
          Log out and use a different account
        </a>
      </div>
    </AuthLayout>
  );
}
